import type { AssessmentSchema } from '../engine/types';
import { BOARD_AI_LITERACY_QUESTIONS } from './board-ai-literacy.questions';
import { buildHubSpotNoteForBoardAiLiteracy } from './board-ai-literacy.hubspot';
import BoardAiLiteracyResult from '../results/BoardAiLiteracyResult';
import BoardAiLiteracyEmail from '../emails/BoardAiLiteracyEmail';

export const boardAiLiteracySchema: AssessmentSchema = {
  id: 'board-ai-literacy',
  version: '1.0.0',
  slug: 'board-ai-literacy',
  title: 'Board AI Literacy Index',
  shortTitle: 'Board AI Literacy',
  description:
    'A 14-question scored assessment of board-level AI literacy and governance maturity for Australian SMB and mid-market businesses.',
  audience: 'Directors, board chairs, CEOs, COOs, and CFOs of Australian SMBs',
  estimatedMinutes: 6,
  questions: BOARD_AI_LITERACY_QUESTIONS,
  scoring: {
    type: 'two-axis',
    axes: [
      { id: 'governance', label: 'Governance Maturity' },
      { id: 'capability', label: 'Director Capability' },
    ],
    compositeWeights: { governance: 0.5, capability: 0.5 },
    bands: [
      {
        id: 'at-risk',
        label: 'At Risk',
        minScore: 0,
        maxScore: 40,
        message:
          'Your board has visible gaps in both governance scaffolding and director capability. This is the typical starting point — it is fixable in 90 days with the right structure.',
      },
      {
        id: 'maturing',
        label: 'Maturing',
        minScore: 41,
        maxScore: 70,
        message:
          'Your board has some of the right scaffolding in place but the strengths are uneven. The 90-day priority is balancing the weaker axis.',
      },
      {
        id: 'mature',
        label: 'Mature',
        minScore: 71,
        maxScore: 100,
        message:
          'Your board is in the top tier for AI literacy and governance maturity among Australian SMBs. You probably do not need a fractional CAIO yet — but a quarterly check-in is worth the discipline.',
      },
    ],
  },
  coverageFlagThreshold: 0.7,
  intakeFields: [
    { name: 'name', label: 'Your name', required: true },
    { name: 'company', label: 'Company', required: true },
    { name: 'phone', label: 'Phone', required: true, type: 'tel' },
    { name: 'role', label: 'Your role', required: false },
  ],
  hubspotMapping: {
    contact: {
      email: (i) => i.email,
      firstname: (i) => i.name?.split(' ')[0],
      lastname: (i) => i.name?.split(' ').slice(1).join(' ') || undefined,
      company: (i) => i.company,
      jobtitle: (i) => i.role,
      lifecyclestage: 'lead',
    },
    note: (result, intake) => buildHubSpotNoteForBoardAiLiteracy(result, intake),
  },
  email: {
    subject: (band) => {
      if (band === 'at-risk') return 'Your Board AI Literacy Index: at-risk band — here is your 90-day plan';
      if (band === 'maturing') return 'Your Board AI Literacy Index: maturing — here is the priority axis';
      return 'Your Board AI Literacy Index: mature band — top 10% of Australian SMB boards';
    },
    template: BoardAiLiteracyEmail,
    fromName: 'Todd Johnson, InnovateX',
  },
  results: {
    component: BoardAiLiteracyResult,
    ctaLabel: 'Book a 30-minute Board AI Briefing',
    ctaUrl: '/contact/?source=board-ai-literacy',
    secondaryCtaLabel: 'Read about Fractional CAIO services',
    secondaryCtaUrl: '/services/fractional-chief-ai-officer/',
  },
  analytics: { eventPrefix: 'board_ai_literacy' },
};
